/**
 * What a call looks like: one person's sound, one person's picture, and a way
 * to make that picture big.
 *
 * Separate from the call itself so the guest page — someone outside the ERP
 * following a link — shows a call exactly the way the ERP does, from the same
 * code rather than a copy of it.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Minimize2, Monitor, Video, X } from "lucide-react";

/** Phone browsers have no `getDisplayMedia`, so for them a share button could only ever fail. */
export const canShareScreen = (): boolean =>
  typeof navigator !== "undefined" &&
  !!(navigator.mediaDevices as MediaDevices | undefined)?.getDisplayMedia;

/** A peer's sound. Separate from their tile on purpose — see the call panel. */
export function AudioSink({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    // `play()` predates promises on some engines (and in jsdom) — wrapping it
    // means a call never dies on a TypeError thrown inside a render effect
    void Promise.resolve(el.play?.()).catch(() => {
      /* autoplay refused until a gesture — the call buttons provide one */
    });
  }, [stream]);
  return <audio ref={ref} autoPlay playsInline />;
}

/** One person's picture. Muted: their sound comes out of `AudioSink`, which
 *  stays mounted even when this tile does not. */
export function PeerTile({
  name,
  stream,
  hasVideo,
  screen,
  onExpand,
}: {
  name: string;
  stream: MediaStream | undefined;
  hasVideo: boolean;
  screen?: boolean;
  onExpand?: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== (stream ?? null)) el.srcObject = stream ?? null;
    // `play()` predates promises on some engines (and in jsdom) — wrapping it
    // means a call never dies on a TypeError thrown inside a render effect
    void Promise.resolve(el.play?.()).catch(() => {
      /* autoplay refused until a gesture — the call buttons provide one */
    });
  }, [stream]);
  const { t } = useTranslation();
  return (
    <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900">
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        className="h-full w-full object-cover"
      />
      {hasVideo ? (
        <>
          {/* A shared screen is unreadable at this size — the whole point of
              looking at it is to read it, so the tile is a way in. Its icon is
              always visible on a touch screen, which has no hover to reveal it. */}
          <button
            type="button"
            onClick={onExpand}
            className="absolute inset-0 flex items-start justify-end p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100"
            title={t("call_enlarge")}
            aria-label={t("call_enlarge")}
          >
            <span className="rounded bg-black/60 p-1 text-gray-100">
              <Maximize2 className="h-3.5 w-3.5" />
            </span>
          </button>
          <div className="pointer-events-none absolute bottom-1 left-1 flex max-w-[90%] items-center gap-1 truncate rounded bg-black/50 px-1.5 py-0.5 text-[11px] text-gray-100">
            {screen && <Monitor className="h-3 w-3 shrink-0" />}
            <span className="truncate">{name}</span>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center px-2 text-center text-sm text-gray-400">
          {name}
        </div>
      )}
    </div>
  );
}

/** Someone's picture, big. For a shared screen this is the only useful size;
 *  the call itself carries on behind it, controls and all. */
export function CallStage({
  name,
  screen,
  stream,
  controls,
  notice,
  onClose,
}: {
  name: string;
  screen: boolean;
  stream: MediaStream | undefined;
  controls: React.ReactNode;
  /** anything the page needs to keep saying while this covers it — a camera
   *  that refused, say. The overlay fills the viewport, so a banner left
   *  behind it would never be read. */
  notice?: React.ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [full, setFull] = useState(false);
  // iPhone browsers can only make a <video> fullscreen, not this box
  const canFullscreen = !!document.fullscreenEnabled;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== (stream ?? null)) el.srcObject = stream ?? null;
    // `play()` predates promises on some engines (and in jsdom) — wrapping it
    // means a call never dies on a TypeError thrown inside a render effect
    void Promise.resolve(el.play?.()).catch(() => {});
  }, [stream]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    const onFullChange = () => setFull(!!document.fullscreenElement);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFullChange);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFullChange);
    };
  }, [onClose]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement)
      void document.exitFullscreen().catch(() => {});
    else void boxRef.current?.requestFullscreen?.().catch(() => {});
  };

  return (
    <div
      ref={boxRef}
      className="fixed inset-0 z-[10001] flex flex-col bg-black"
    >
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-100">
        {screen ? (
          <Monitor className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <Video className="h-4 w-4 shrink-0 text-gray-400" />
        )}
        <span className="min-w-0 flex-1 truncate">{name}</span>
        {canFullscreen && (
          <button
            type="button"
            onClick={toggleFullscreen}
            className="rounded p-1.5 text-gray-300 hover:bg-gray-800 hover:text-white max-md:p-3.5"
            title={t("call_fullscreen")}
            aria-label={t("call_fullscreen")}
          >
            {full ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1.5 text-gray-300 hover:bg-gray-800 hover:text-white max-md:p-3.5"
          title={t("close")}
          aria-label={t("close")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {/* object-contain: a shared screen must be shown whole, never cropped */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="min-h-0 flex-1 bg-black object-contain"
      />
      {notice && <div className="px-3 pt-2">{notice}</div>}
      <div className="flex flex-wrap items-center justify-center gap-2 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        {controls}
      </div>
    </div>
  );
}
